import { styled } from "styled-components";

import { Button } from "../Button";
import { Icon } from "../Icon";
import { textareaFocusStyles } from "../Textarea/styled";

import { TextareaContainer } from "../Textarea";

interface ButtonContainerProps {
  $ready: boolean;
}

export const ButtonContainer = styled.label<ButtonContainerProps>(
  ({ theme, $ready }) => {
    return {
      border: "1px solid rgba(115,115,118,0.3)",
      backgroundColor: theme.colors.white,
      boxShadow: `0 1px 12px ${theme.shadows[2]}`,
      transition: "border-color 250ms",
      resize: "none",
      display: "inline-flex",
      justifyContent: "center",
      alignItems: "center",
      width: theme.sizes.md,
      borderRadius: theme.radii[1],
      boxSizing: "border-box",
      cursor: "text",
      // Host pages (Bootstrap, etc.) style raw `label`s. Neutralize that here
      // so light-DOM previews keep the same box as the shadowed widget.
      margin: 0,
      maxWidth: "none",
      fontWeight: "inherit",

      [`& ${BubbleContainer}`]: {
        transform: "scale(1)",
        transition: "transform 250ms, background-color 250ms",
      },
      ...($ready
        ? {
            [`& ${BubbleContainer}`]: {
              transform: "scale(1)",
              cursor: "pointer",
            },
          }
        : {}),
    };
  },
);

export const Container = styled.div(({ theme }) => {
  return {
    display: "flex",
    boxShadow: `0 1px 12px ${theme.shadows[2]}`,
    transition: "box-shadow 250ms, border-color 250ms",

    [`& ${TextareaContainer}`]: {
      border: "1px solid rgba(115,115,118,0.3)",
      backgroundColor: theme.colors.white,
      transition: "border-color 250ms",
      resize: "none",
      boxSizing: "border-box",
      margin: 0,
      borderRightWidth: 0,
      borderTopRightRadius: 0,
      borderBottomRightRadius: 0,
      paddingRight: 7,
      boxShadow: "none",
    },

    [`& ${ButtonContainer}`]: {
      height: "inherit",
      borderLeftWidth: 0,
      borderTopLeftRadius: 0,
      borderBottomLeftRadius: 0,
    },

    [`& ${Icon.Frame}`]: {
      color: theme.colors.white,
    },

    [`& ${TextareaContainer}:focus`]: {
      ...textareaFocusStyles,
      borderRightWidth: 0,
    },

    [`& ${TextareaContainer}:focus + ${ButtonContainer}`]: {
      ...textareaFocusStyles,
      borderLeftWidth: 0,
    },
  };
});

interface BubbleProps {
  /** Default is large */
  $size?: "small" | "large";
}

export const BubbleContainer = styled(Button.BaseButton)<BubbleProps>(({
  theme,
  $size = "large",
}) => {
  const largeStyles = {
    height: theme.sizes.xl,
    width: theme.sizes.xl,
    border: `1px solid ${theme.shadows[4]}`,
    boxShadow: `0 1px 6px ${theme.shadows[6]}, 0 2px 24px ${theme.shadows[8]}`,
    [`& ${Icon.Frame}`]: {
      width: theme.sizes.sm,
      height: theme.sizes.sm,
    },
  };

  const smallStyles = {
    height: theme.sizes.xs,
    width: theme.sizes.xs,
    [`& ${Icon.Frame}`]: {
      width: theme.sizes.xxs,
      height: theme.sizes.xxs,
    },
  };

  return {
    transform: "scale(1)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: theme.radii.round,
    backgroundColor: theme.colors.primary,
    transition: "background-color 250ms",

    "&:hover": {
      backgroundColor: theme.colors.primaryDark,
    },
    ...($size === "large" ? largeStyles : smallStyles),
  };
});
